import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, NavLink, useLocation } from "react-router-dom";
import { logIn, selectAuthInfo } from "store/authSlice";
import { clearCurrentBoard } from "store/currentBoardSlice";
import { addToast } from "store/toastsSlice";

import Api from "api/Api";
import Button from "layout/atoms/Button";
// import GoogleButton from "layout/atoms/GoogleButton";
import ContentBox from "layout/atoms/ContentBox";
import Form from "layout/atoms/Form";
import InputText from "layout/atoms/InputText";
import Link from "layout/atoms/Link";
import Logo from "layout/atoms/Logo";
import MainContainer from "layout/atoms/MainContainer";

const LoginForm = (props) => {
  const dispatch = useDispatch();
  const authInfo = useSelector(selectAuthInfo);
  const location = useLocation();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  let api = new Api();
  const { t } = useTranslation("translation", {
    keyPrefix: "loginForm",
  });
  const {
    register,
    handleSubmit,
    formState,
    formState: { errors, isSubmitSuccessful },
    reset,
  } = useForm();

  const isSessionExpired =
    new URLSearchParams(location.search).get("expired") === "true";

  const handleLogin = (data) => {
    dispatch(logIn(data))
      .unwrap()
      .then((loginResponse) => {
        console.log({ loginResponse });
        // Success - the thunk already set the token and updated state
      })
      .catch((error) => {
        console.error("Login failed:", error);
        dispatch(
          addToast({
            title: "toasts.logIn.title",
            description: "toasts.logIn.description",
            type: "error",
          }),
        );
      });
  };

  const handleGoogleLogin = () => {
    setIsGoogleLoading(true);
    // Initialize Google Sign-In
    // This is a placeholder - you'll need to:
    // 1. Add Google OAuth client library to your HTML
    // 2. Initialize google.accounts.id with your client ID
    // 3. Handle the credential response

    if (window.google && window.google.accounts) {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          setIsGoogleLoading(false);
          dispatch(
            addToast({
              title: "toasts.logIn.title",
              description: "Google sign-in was closed or is unavailable",
              type: "error",
            }),
          );
        }
      });
    } else {
      setIsGoogleLoading(false);
      dispatch(
        addToast({
          title: "toasts.logIn.title",
          description:
            "Google sign-in is not available. Please use email login.",
          type: "error",
        }),
      );
    }
  };

  // Handle Google credential response
  const handleGoogleResponse = (response) => {
    setIsGoogleLoading(true);
    api
      .loginWithGoogle(response.credential)
      .then((loginResponse) => {
        localStorage.setItem("authToken", loginResponse.token);
        localStorage.setItem("authTokenExpiresAt", loginResponse.expiresAt);
        dispatch(logIn({ email: "google-user" }));
      })
      .catch((error) => {
        setIsGoogleLoading(false);
        dispatch(
          addToast({
            title: "toasts.logIn.title",
            description: "toasts.logIn.description",
            type: "error",
          }),
        );
      });
  };

  useEffect(() => {
    dispatch(clearCurrentBoard());

    if (isSessionExpired) {
      dispatch(
        addToast({
          title: "toasts.sessionExpired.title",
          description: "toasts.sessionExpired.description",
          type: "warning",
        }),
      );
    }

    // Initialize Google Sign-In
    if (window.google && window.google.accounts) {
      window.google.accounts.id.initialize({
        client_id:
          process.env.REACT_APP_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID",
        callback: handleGoogleResponse,
      });
    }
  }, [dispatch, isSessionExpired]);

  return authInfo.isUserLoggedIn ? (
    <Navigate to={`/`} />
  ) : (
    <MainContainer alignCenter>
      <Logo circle />
      <Form onSubmit={handleSubmit(handleLogin)}>
        <ContentBox
          size="s"
          centeredHeader
          header={<h2>{t("title")}</h2>}
          footer={
            <>
              <NavLink to="/password-reset">{t("forgotPassword")}</NavLink>
              <Button
                type="submit"
                size="lg"
                color="primary"
                loading={authInfo.status === "loading"}
              >
                {t("button")}
              </Button>
            </>
          }
        >
      {/*    <GoogleButton
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading}
            loading={isGoogleLoading}
          >
            {t("googleButton")}
          </GoogleButton>

          <div
            style={{
              textAlign: "center",
              margin: "20px 0",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                position: "relative",
                display: "inline-block",
                padding: "0 10px",
                background: "white",
                zIndex: 1,
                fontSize: "12px",
                color: "#5f6368",
                fontWeight: 500,
              }}
            >
              {t("orDivider")}
            </span>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '1px',
              background: '#dadce0',
              zIndex: 0
            }} />
          </div>*/}

          <InputText
            type="text"
            name="email"
            label={t("emailLabel")}
            register={register}
            errors={errors.email}
            validation={{
              required: true,
              minLength: 6,
              maxLength: 50,
            }}
          />
          <InputText
            type="password"
            name="password"
            label={t("passwordLabel")}
            register={register}
            errors={errors.password}
            validation={{
              required: true,
              minLength: 6,
              maxLength: 50,
            }}
            autocomplete="on"
          />
        </ContentBox>
        <Link center>
          {t("register")} <NavLink to="/signup">{t("registerLink")}</NavLink>
        </Link>
      </Form>
    </MainContainer>
  );
};

export default LoginForm;
